import React, { useState, useEffect, useRef, useMemo } from "react";
import { PhoneNumberUtil } from "google-libphonenumber";
import moment from "moment";
import {
  removeSplCharacters,
  validateCodeEmail,
  validatePhoneNo,
} from "../../utils/globals";
import ProfileService from "../../services/profileService";
import { phone_codes } from "../../utils/locales";
import { USER_MSG_CHAR_LIMIT } from "../../constants/XoxoCodeCampaignConstants";
import {
  MOBILE_NO_ERROR,
  defaultMinQuantity,
} from "../../constants/AppConstant";
import SendMode from "./SendMode";
import { defaultMaxQuantity } from "../../constants/AppConstant";
import { NotificationManager } from "react-notifications";
import {
  Dropdown,
  Input,
  Typography,
  Icon,
  Checkbox,
  Button,
  DatePicker,
} from "@plum-xoxo/admin-dsm";
import styled from "styled-components";
import { UncontrolledTooltip } from "reactstrap";
import BrandApprovalWarningBanner from "../BrandApprovalWarningBanner";
import ReactQuillEditor from "../../components/ReactQuillEditor";

const maxPlumProOrderQuantity = 100;
const maxPlumProOrderQuantityWithPdf = 15;

const StyledDiv = styled.div.attrs()`
  width: 48%;
`;
const StyledDivGap = styled.div.attrs()`
  gap: 20px;
`;
const StyledInput = styled(Input).attrs()`
  width: 48%;
`;
const StyledDropDownContainer = styled.div.attrs()`
  width: 140px;
`;
const StyledPhoneInput = styled(Input).attrs()`
  width: 65%;
`;

const StyledNoOfCodesInput = styled(Input).attrs()`
  width: 130px;
  #no-of-codes {
    text-align: center;
  }
`;
const StyledCheckbox = styled(Checkbox).attrs()`
  margin-bottom: 0;
  white-space: nowrap;
`;

const StyledDropdown = styled(Dropdown).attrs()`
  width: 48%;
`;
const SINGLE_RECIPIENT_CODE = {
  name: "",
  email: "",
  codeValue: "",
  quantity: "1",
  total: 0,
  to_phone_code: "",
  to_phone_number: "",
  notes: "",
  user_message: "",
  date: moment().format("DD-MM-YYYY"),
};

const SINGLE_RECIPIENT_POINTS = {
  name: "",
  email: "",
  amount: "",
  total: 0,
  to_phone_code: "",
  to_phone_number: "",
  notes: "",
  user_message: "",
  date: moment().format("DD-MM-YYYY"),
};

const SINGLE_RECIPIENT_LINK = {
  name: "",
  email: "",
  total: 0,
  to_phone_code: "",
  to_phone_number: "",
  user_message: "",
  notes: "",
};

const SINGLE_RECIPIENT_PRO = {
  receiver_name: "",
  email: "",
  total: 0,
  to_phone_code: "",
  to_phone_number: "",
  user_message: "",
  po_number: "",
  country: "",
  denomination: "",
  voucherDetail: "",
  quantity: "1",
  is_variable: 0,
  denominationOption: [],
  max_order_quantity: defaultMaxQuantity,
  min_order_quantity: defaultMinQuantity,
  price: {
    currencyValue: 0,
    discounted_value: 0,
    surcharged_value: 0,
    final_amount: 0,
    unit_price: 0,
  },
};

const SINGLE_ERROR_CODE = {
  name: "",
  email: "",
  amount: "",
  user_message: "",
  to_phone_code: "",
  to_phone_number: "",
};

const SINGLE_ERROR_POINTS = {
  name: "",
  email: "",
  codeValue: "",
  quantity: "",
  user_message: "",
  to_phone_code: "",
  to_phone_number: "",
};

const SINGLE_ERROR_LINK = {
  name: "",
  email: "",
  total: 0,
  to_phone_code: "",
  to_phone_number: "",
  user_message: "",
};

const SINGLE_ERROR_PRO = {
  receiver_name: "",
  email: "",
  total: 0,
  to_phone_code: "",
  to_phone_number: "",
  user_message: "",
  country: "",
  po_number: "",
  denomination: "",
  voucherDetail: "",
  quantity: "",
};

const getCustomIcon = () => {
  return <Icon iconName="PlumCalendar" color="dark" shade="c200" size="s" />;
};

function PriceBreakupPro({ currencyCode, recipient }) {
  return (
    <div className="">
      <div className="d-flex">
        <Typography.HelperText
          className="col-xs-6"
          color="light"
          shade="c00"
          component={"span"}
        >
          Item Total:
        </Typography.HelperText>
        <Typography.HelperText
          className="col-xs-6 ml-4"
          color="light"
          shade="c00"
        >
          <Typography.ButtonText>
            {currencyCode}{" "}
            {parseFloat(recipient?.price?.currencyValue).toFixed(2)}
          </Typography.ButtonText>
          &nbsp;
        </Typography.HelperText>
      </div>
      {recipient.spl_discount ? (
        <div className="d-flex">
          <Typography.HelperText className="col-xs-6" color="light" shade="c00">
            Discount:
          </Typography.HelperText>
          <Typography.HelperText
            className="col-xs-6 ml-4"
            color="light"
            shade="c00"
          >
            <Typography.ButtonText>
              {currencyCode}{" "}
              {parseFloat(recipient?.price?.discounted_value).toFixed(2)}
            </Typography.ButtonText>
            &nbsp;
          </Typography.HelperText>
        </div>
      ) : null}
      {recipient.spl_charge ? (
        <div className="d-flex">
          <Typography.HelperText className="col-xs-6" color="light" shade="c00">
            Surcharge:
          </Typography.HelperText>
          <Typography.HelperText
            className="col-xs-6 ml-4"
            color="light"
            shade="c00"
          >
            <Typography.ButtonText>
              {currencyCode}{" "}
              {parseFloat(recipient?.price?.surcharged_value).toFixed(2)}{" "}
            </Typography.ButtonText>
            &nbsp;
          </Typography.HelperText>
        </div>
      ) : null}
      <div
        className="d-flex pt-10"
        style={{
          borderTop: "1px solid",
        }}
      >
        <Typography.HelperText className="col-xs-6" color="light" shade="c00">
          Total Payable:
        </Typography.HelperText>
        <Typography.HelperText
          className="col-xs-6 ml-4"
          color="light"
          shade="c00"
        >
          <Typography.ButtonText>
            {currencyCode}{" "}
            {parseFloat(recipient?.price?.final_amount.toFixed(2)).toFixed(2) ||
              "Total Amount"}
          </Typography.ButtonText>
          &nbsp;
        </Typography.HelperText>
      </div>
    </div>
  );
}

const RecepientForm = (props) => {
  const {
    currencyCode = "ISD",
    sendXoxoCodePdf = 1,
    isEditUserEnabled = false,
    setIsEdituserEnabled,
    authentication_type,
    accountReducer,
    addMore,
    setAddMore,
    editUserId,
    setEditUserId,
    sendMode = [],
    setSendMode,
    recipientData,
    setRecipientData,
    setDisabled,
    rewardType,
    setFinalTotal,
    countryList,
    selectedCountry,
    countryListLoading,
    voucherList,
    productLoader,
    calculatePayable,
    setSelectedCountry,
    kybStatus,
    selectedSite,
  } = props;

  const SINGLE_RECIPIENT = [
    SINGLE_RECIPIENT_POINTS,
    SINGLE_RECIPIENT_CODE,
    SINGLE_RECIPIENT_LINK,
    SINGLE_RECIPIENT_PRO,
  ];
  const SINGLE_ERROR = [
    SINGLE_ERROR_POINTS,
    SINGLE_ERROR_CODE,
    SINGLE_ERROR_LINK,
    SINGLE_ERROR_PRO,
  ];
  const settingsRef = useRef();
  const [recipient, setRecipient] = useState(SINGLE_RECIPIENT[rewardType - 1]);
  const [error, setError] = useState(SINGLE_ERROR);
  const [showSettings, setShowSettings] = useState(false);
  const [phoneCodeOptions, setPhoneCodeOptions] = useState([]);
  const [phonecodeDetails, setPhonecodeDetails] = useState("");
  const [codeQuantity, setCodesQuantity] = useState(1);
  const [initialTotal, setInitialTotal] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState({});
  const [poError, setPoError] = useState(false);
  const [selectedDenomination, setSelectedDenomination] = useState({
    label: "",
    value: 0,
  });
  const [isValidating, setIsValidating] = useState(false);
  const hasRun = useRef(null);
  const indiaPhoneCode =
    phoneCodeOptions?.filter((data) => data.value === 99) || [];

  useEffect(() => {
    let data;
    if (props.location?.state) {
      try {
        data = JSON.parse(props.location.state);
      } catch (e) {
        console.error("Error in parsing location state", e);
      }
      const campaignBudget = data?.campaignBudget || "";
      SINGLE_RECIPIENT_CODE.codeValue = campaignBudget;
    }
  }, []);

  const priceBreakupMemo = useMemo(
    () => <PriceBreakupPro currencyCode={currencyCode} recipient={recipient} />,
    [currencyCode, recipient]
  );

  const inputChangeHandler = (event) => {
    let recipientState = { ...recipient };
    let errorState = { ...error };
    let target = event.target;
    let tag = target.name;
    let reg = /^\d+$/;
    if (
      (tag == "codeValue" ||
        tag == "to_phone_number" ||
        tag === "quantity" ||
        tag === "amount") &&
      !(event.target.value.length == 0 && event.target.value == "") &&
      !(reg.test(event.target.value) && parseInt(event.target.value) != 0)
    ) {
      return;
    }
    if (tag == "to_phone_number" && event.target.value.length > 16) {
      return;
    }
    if (tag == "notes" && event.target.value.length > 150) {
      return;
    }

    if (tag === "to_phone_code") {
      recipientState[tag] = event;
    } else if (tag === "name" || tag === "receiver_name") {
      if (event.target.value.length <= 70) {
        let nameCamelCase =
          event &&
          event.target &&
          event.target.value &&
          event.target.value
            .trimStart()
            .toLowerCase()
            .replace(/\s(.)/g, ($1) => $1.toUpperCase())
            .replace(/^(.)/g, ($1) => $1.toUpperCase());
        recipientState[tag] = nameCamelCase;
        errorState[tag] ? (errorState[tag] = "") : null;
      }
    } else if (tag === "quantity") {
      recipientState[tag] = event.target.value;
      if (codeQuantity > 15) {
        errorState[tag] = "Maximum quantity is 15";
      } else {
        errorState[tag] = "";
      }
    } else if (tag === "codeValue") {
      if (event.target.value.length <= 10) {
        recipientState[tag] = event.target.value;
        recipientState.codeValue
          ? (recipientState.total = recipientState.codeValue * codeQuantity)
          : 0;
        errorState["codeValue"] = "";
      } else {
        errorState["codeValue"] = "Max limit reached";
      }
    } else if (tag == "to_phone_number") {
      recipientState[tag] = event.target.value;
      errorState[tag] = "";
    } else if (tag == "email") {
      if (event.target.value.length <= 150) {
        recipientState[tag] = event.target.value;
        errorState["email"] = "";
      }
    } else if (tag == "amount") {
      if (event.target.value.length <= 20) {
        recipientState[tag] = event.target.value;
        errorState["amount"] = "";
        recipientState["total"] = event.target.value || 0;
      } else {
        errorState["amount"] = "Max limit reached.";
      }
    } else {
      recipientState[tag] = event.target.value;
      errorState[tag] ? (errorState[tag] = "") : null;
    }
    setRecipient({ ...recipientState, quantity: codeQuantity });
    setError({ ...errorState });
  };

  const addRecipient = () => {
    if (setAddMore) setAddMore(false);
    const { userDataIndex, setUserDataIndex, updateScheduleCount } = props;
    let temp = { ...recipient };
    temp.email = temp.email.trim();
    temp.codeValue && temp.quantity
      ? (temp.total = temp.codeValue * temp.quantity)
      : 0;
    temp.id = userDataIndex;

    if (rewardType === 3) temp.total = selectedSite?.denomination_value;
    const finalData = [...recipientData, temp];
    setRecipientData(finalData);
    setUserDataIndex(userDataIndex + 1);
    setAddMore(false);
    setDisabled(false);

    if (rewardType === 1 || rewardType === 2) {
      updateScheduleCount(finalData);
      calculateFinalTotal("add", temp.total);
    }
    // props.checkForSmsPreview(finalData);
  };

  const editRecipient = () => {
    const { userDataIndex, editUserId, setUserDataIndex, updateScheduleCount } =
      props;
    const singleRecipientData = { ...recipient };
    const temp = recipientData;
    calculateFinalTotal("edit", singleRecipientData.total);
    const indexNo = temp.findIndex((user) => user.id === editUserId);
    temp.splice(indexNo, 1);
    let userData = { ...singleRecipientData };
    if (sendMode === "email") {
      userData.to_phone_number = "";
    } else if (sendMode === "sms") {
      userData.email = "";
    }
    userData.email = userData?.email?.trim();
    userData.id = userDataIndex;
    const finalData = [...recipientData, userData];

    setRecipientData(finalData);
    setIsEdituserEnabled(false);
    setEditUserId(0);
    setUserDataIndex(userDataIndex + 1);
    setDisabled(false);

    if (rewardType === 1 || rewardType === 2) {
      updateScheduleCount(finalData);
      calculateFinalTotal("add", temp.total);
    }
    // props.checkForSmsPreview(finalData);
  };
  const calculateFinalTotal = (typeOfOperation, userTotal) => {
    const { finalTotal } = props;

    const initialTotal = initialTotal;

    let tempTotal = finalTotal;

    if (typeOfOperation === "add") {
      tempTotal += userTotal;
    } else if (typeOfOperation === "edit") {
      tempTotal = tempTotal - initialTotal + userTotal;
    }
    if (rewardType == 1 || rewardType == 2) setFinalTotal(tempTotal);
  };

  const validateData = async () => {
    setIsValidating(true);
    const phoneUtil = PhoneNumberUtil.getInstance();
    let recipientState = { ...recipient };
    let errorState = { ...error };
    let isError = false;
    if (recipientState.name == "") {
      errorState.name = "Name cannot be left blank.";
      isError = true;
    }

    if (
      rewardType === 2 &&
      recipientState.email == "" &&
      authentication_type == 2
    ) {
      errorState.email = "Email address cannot be left blank. ";
      isError = true;
    }

    if (rewardType === 4) {
      if (recipient.denomination === "") {
        errorState.denomination = "Value of product cannot be left blank. ";
        isError = true;
      }

      if (recipient.voucherDetail === "") {
        errorState.voucherDetail = "Product cannot be left blank.";
        isError = true;
      }

      if (recipient.email === "" && recipient.to_phone_number === "") {
        errorState.email = "Atleast email or phone number is required.";
        isError = true;
      }

      if (recipient.verify_mobile && recipient.to_phone_number === "") {
        errorState.to_phone_number = "Mobile number cannot be left blank.";
        isError = true;
      }

      if (
        !isEditUserEnabled &&
        checkForQtyCheckProducts(
          selectedProduct,
          recipient.email,
          recipient.to_phone_number
        )
      ) {
        errorState[
          "voucherDetail"
        ] = `More than 1 voucher is not allowed for ${selectedProduct.label} product.`;
        isError = true;
      } else if (
        isEditUserEnabled &&
        checkForQtyCheckProducts(
          selectedProduct,
          recipient.email,
          recipient.to_phone_number,
          recipient.id
        )
      ) {
        errorState[
          "voucherDetail"
        ] = `More than 1 voucher is not allowed for ${selectedProduct.label} product.`;
        isError = true;
      }
    }
    if (
      rewardType !== 4 &&
      sendMode?.includes("email") &&
      recipientState.email == ""
    ) {
      errorState.email = "Email address cannot be left blank. ";
      isError = true;
    }
    if (
      rewardType !== 4 &&
      (sendMode?.includes("sms") ||
        sendMode?.includes("whatsapp") ||
        rewardType === 4) &&
      recipientState.to_phone_number == ""
    ) {
      errorState.to_phone_number = "Mobile number cannot be left blank.";
      isError = true;
    }
    //validate reward user
    if (
      rewardType === 2 &&
      recipientState.email !== "" &&
      authentication_type == 2 &&
      !accountReducer?.allowedDomains?.includes(
        recipientState.email?.split("@")[1]
      )
    ) {
      errorState.email =
        "The email domain isn’t matching the domain(s) allowed for your account. ";
      isError = true;
    }
    if (recipientState.to_phone_number === "" && recipientState.email.trim()) {
      if (
        (sendMode?.includes("email") || rewardType === 4) &&
        recipientState.email !== "" &&
        !(await validateCodeEmail(recipientState.email))
      ) {
        errorState.email = "Enter valid email address.";
        isError = true;
      }
      if (recipientState.email == "") {
        errorState.email = "Email address cannot be left blank.";
        isError = true;
      }
    } else if (
      recipientState.to_phone_number !== "" &&
      recipientState.email === ""
    ) {
      if (
        (sendMode?.includes("sms") ||
          sendMode?.includes("whatsapp") ||
          rewardType === 4) &&
        recipientState.to_phone_code !== "" &&
        recipientState.to_phone_code.phonecode !== "" &&
        recipientState.to_phone_number == ""
      ) {
        errorState.to_phone_number = "Mobile number cannot be left blank.";
        isError = true;
      } else if (
        recipientState.to_phone_code &&
        recipientState.to_phone_number &&
        recipientState.to_phone_code.phonecode &&
        !phoneUtil.isValidNumberForRegion(
          phoneUtil.parse(
            `${
              recipientState.to_phone_code.phonecode
            }${recipientState.to_phone_number.trim()}`,
            `${recipientState.to_phone_code.iso}`
          ),
          `${recipientState.to_phone_code.iso}`
        )
      ) {
        errorState.to_phone_number = MOBILE_NO_ERROR;
        isError = true;
      }
    } else if (recipientState.email && recipientState.to_phone_number) {
      if (
        (sendMode?.includes("email") || rewardType === 4) &&
        recipientState.email !== "" &&
        !(await validateCodeEmail(recipientState.email))
      ) {
        errorState.email = "Enter valid email address.";
        isError = true;
      }
      if (recipientState.email == "") {
        errorState.email = "Email address cannot be left blank.";
        isError = true;
      }

      if (
        (sendMode?.includes("sms") ||
          sendMode?.includes("whatsapp") ||
          rewardType === 4) &&
        recipientState.to_phone_code !== "" &&
        recipientState.to_phone_code.phonecode !== "" &&
        recipientState.to_phone_number == ""
      ) {
        errorState.to_phone_number = "Mobile number cannot be left blank.";
        isError = true;
      } else if (
        (sendMode?.includes("sms") ||
          sendMode?.includes("whatsapp") ||
          rewardType === 4) &&
        recipientState.to_phone_code &&
        recipientState.to_phone_code.phonecode &&
        recipientState.to_phone_number &&
        !phoneUtil.isValidNumberForRegion(
          phoneUtil.parse(
            `${
              recipientState.to_phone_code.phonecode
            }${recipientState.to_phone_number.trim()}`,
            `${recipientState.to_phone_code.iso}`
          ),
          `${recipientState.to_phone_code.iso}`
        )
      ) {
        errorState.to_phone_number = MOBILE_NO_ERROR;
        isError = true;
      }
    }

    if (recipientState.codeValue == "" || recipientState.codeValue <= 0) {
      errorState.codeValue = "Enter a value greater than 0";
      isError = true;
    }
    if (recipientState.quantity == "" || recipientState.quantity <= 0) {
      errorState.quantity = "Enter Valid Quantity";
      isError = true;
    } else if (
      sendXoxoCodePdf === 1 &&
      (rewardType === 2 || rewardType === 4) &&
      recipientState.quantity > 15
    ) {
      errorState.quantity = "Maximum quantity is 15";
      isError = true;
    } else if (
      sendXoxoCodePdf === 0 &&
      ((rewardType === 2 && recipientState.quantity > 200) ||
        (rewardType === 4 && recipientState.quantity > 100))
    ) {
      errorState.quantity = `Maximum quantity is ${
        rewardType === 2 ? 200 : 100
      }`;
      isError = true;
    }
    if (
      rewardType === 4 &&
      recipient.is_variable &&
      (selectedProduct.min_price > recipient.denomination.value ||
        selectedProduct.max_price < recipient.denomination.value)
    ) {
      isError = true;
    }
    if (
      recipientState.to_phone_code == "" &&
      recipientState.to_phone_code.phonecode == "" &&
      recipientState.to_phone_number !== ""
    ) {
      errorState.to_phone_code = "Select ISO Code";
      isError = true;
    }
    if (recipientState.amount == "") {
      errorState.amount = "Please enter a value greater than 0";
      isError = true;
    }
    setIsValidating(false);
    setError({ ...errorState });
    if (!isError && !isEditUserEnabled && !poError) {
      addRecipient();
    } else if (!isError && isEditUserEnabled) {
      editRecipient();
    }
  };

  const inputSelectHandler = (event) => {
    let recipientState = { ...recipient };
    let errorState = { ...error };
    recipientState["to_phone_code"] = event;
    delete errorState["to_phone_code"];
    setRecipient({ ...recipientState });
    setError({ ...errorState });
  };

  const setIsdCodes = () => {
    let PhoneCodeOptions =
      phone_codes &&
      phone_codes.map((item, index) => {
        let obj = item;
        obj.label = item.iso + " " + item.phonecode;
        obj.value = item.country_id;
        return obj;
      });
    setPhoneCodeOptions(PhoneCodeOptions);
  };

  const getPhonecodeBasedIP = () => {
    ProfileService.fetchCountryBasedonIP().then((response) => {
      if (response && response.plumApi && response.plumApi.output) {
        const ipBasedOutput = JSON.parse(response.plumApi.output);
        const countryCode = ipBasedOutput.country_code;
        const phonecodeDetails = phone_codes.find(
          (item) => item.iso == countryCode
        );
        if (phonecodeDetails) {
          setRecipient((recipient) => {
            const recipientState = { ...recipient };
            recipientState["to_phone_code"] =
              recipient["to_phone_code"] || phonecodeDetails;
            return { ...recipientState };
          });
          setPhonecodeDetails((phonecodeDetails) => phonecodeDetails);
        }
      }
    });
  };

  const handleDateChange = (value) => {
    let recipientState = { ...recipient };
    let errorState = { ...error };
    recipientState["date"] = value ? moment(value).format("DD-MM-YYYY") : null;
    errorState["date"] = "";
    errorState["show_date"] = false;
    setRecipient({ ...recipientState });
    setError({ ...errorState });
  };

  const handleCancel = () => {
    const recipientState = { ...SINGLE_RECIPIENT[rewardType - 1] };
    recipientState["to_phone_code"] = phonecodeDetails;
    setAddMore(false);
    setIsEdituserEnabled(false);
    setEditUserId(0);
    setRecipient({ ...recipientState });
    setError({});
    setDisabled(false);
  };

  const validatePoNumber = (event) => {
    setPoError(true);
    let errorData = error;

    let poNumber = event.target.value;
    for (let i = 0; i < recipientData.length; i++) {
      if (poNumber != "" && recipientData[i].po_number == poNumber) {
        errorData.po_number = "PO number should be unique.";
        setPoError(true);
        setError(errorData);
        return;
      }
    }

    const { getPoNumbers } = props;
    getPoNumbers(poNumber).then((existingPoNumbers) => {
      if (existingPoNumbers && existingPoNumbers.includes(poNumber)) {
        errorData.po_number = "PO number already exists.";
        setPoError(true);
        setError(errorData);
        return;
      } else {
        errorData.po_number = "";
      }
    });

    setPoError(false);
  };
  const onChangeDenomination = (denValue) => {
    setSelectedDenomination(denValue);
    let recipent_values = recipient;
    recipent_values.denomination = denValue;
    if (
      recipent_values.is_variable &&
      (denValue.value < selectedProduct?.min_price ||
        denValue.value > selectedProduct?.max_price)
    ) {
      error.denomination = `Between ${selectedProduct?.min_price} and ${selectedProduct?.max_price}`;
    } else error.denomination = "";

    recipent_values.total = denValue.value * recipient.quantity;
    setRecipient(recipent_values);
    setError(error);
    handleItemTotal(recipent_values);
  };

  const checkForQtyCheckProducts = (selectedProduct, email, phoneNo, id) => {
    let localRecipientData = recipientData.filter((item) => item.id !== id);
    if (id) {
      if (
        selectedProduct.max_order_quantity &&
        selectedProduct.max_order_quantity > 0
      ) {
        const indexNo = localRecipientData.findIndex((item) => {
          return (
            ((email && item.email === email) ||
              (phoneNo && item.to_phone_number === phoneNo)) &&
            item.product_id === selectedProduct.value
          );
        });
        return indexNo !== -1;
      }
      // recipientOldStateForThatId=> Skip instead check for newStateObj
    } else {
      if (
        selectedProduct.max_order_quantity &&
        selectedProduct.max_order_quantity > 0
      ) {
        const indexNo = recipientData.findIndex((item) => {
          return (
            ((email && item.email === email) ||
              (phoneNo && item.to_phone_number === phoneNo)) &&
            item.product_id === selectedProduct.value
          );
        });
        return indexNo !== -1;
      }
    }
  };

  const onProductChange = async (selectedProduct) => {
    let recipient_values = recipient;
    const {
      accountReducer: {
        clientId = 0,
        userBalance: { currencyCode = "INR" } = {},
      } = {},
    } = props;
    setSelectedProduct(selectedProduct);
    let productBaseConversionValue = 1;
    const productClientCurrencyConversion =
      await ProfileService.getProductToClientBaseConversion({
        source_currency_code: selectedProduct?.currency_code || "",
        client_id: clientId,
        target_currency_code: currencyCode,
        amount: 1,
      });

    if (
      productClientCurrencyConversion &&
      productClientCurrencyConversion.Error
    ) {
      NotificationManager.error("Couldn`t fetch conversion value", "Error");
      return;
    }
    productBaseConversionValue =
      productClientCurrencyConversion.Result.converted_amount;

    recipient_values.voucherDetail = selectedProduct;
    recipient_values.name = selectedProduct.label;
    recipient_values.product_id = selectedProduct.value;
    recipient_values.denominationOption = selectedProduct.denomination;
    recipient_values.product_to_base_conversion = productBaseConversionValue;
    recipient_values.currency_id = selectedProduct.currency_id;
    recipient_values.spl_charge = selectedProduct.spl_charge;
    recipient_values.spl_discount = selectedProduct.spl_discount;
    recipient_values.is_variable = selectedProduct.is_variable;
    recipient_values.conversion_ratio = selectedProduct.conversion_ratio;
    recipient_values.denomination = "";
    recipient_values.verify_mobile = selectedProduct?.verify_mobile;
    recipient_values.max_order_quantity =
      selectedProduct.max_order_quantity === 0
        ? sendXoxoCodePdf === 1
          ? 15
          : 100
        : selectedProduct?.max_order_quantity;
    recipient_values.min_order_quantity = selectedProduct.min_order_quantity;

    if (
      selectedProduct.max_order_quantity &&
      selectedProduct.max_order_quantity > 1
    ) {
      recipient_values.quantity =
        selectedProduct.min_order_quantity > 0
          ? selectedProduct.min_order_quantity
          : 1;
      error.quantity = "";
    } else if (
      selectedProduct.max_order_quantity &&
      selectedProduct.max_order_quantity == 1
    ) {
      recipient_values.quantity = selectedProduct.max_order_quantity;
      error.quantity = "";
    } else {
      recipient_values.quantity = defaultMinQuantity;
      error.quantity = "";
    }
    setCodesQuantity(recipient_values.quantity);
    setError({
      ...error,
      denomination: "",
      voucherDetail: "",
      to_phone_number: "",
      quantity: "",
    });
    setRecipient({
      ...recipient_values,
      total: 0,
      denomination: { label: "", value: "" },
    });
    handleItemTotal({ ...recipient_values, total: 0, denomination: "" });
  };

  const handleItemTotal = (recipientData) => {
    let recipient_values = recipientData;
    let itemTotalSum = 0;
    if (recipient_values?.denomination?.value && recipient_values?.quantity) {
      recipient_values.price.currencyValue = (
        recipient_values?.quantity *
        recipient_values?.denomination?.value *
        recipient_values?.product_to_base_conversion
      ).toFixed(2);
      if (recipient_values?.spl_discount !== null) {
        recipient_values.price.discounted_value = (
          (parseFloat(recipient_values?.price?.currencyValue) *
            parseFloat(recipient_values?.spl_discount)) /
          100
        ).toFixed(2);
      }
      if (recipient_values?.spl_charge != null) {
        recipient_values.price.surcharged_value = (
          (parseFloat(recipient_values?.price?.currencyValue) *
            parseFloat(recipient_values?.spl_charge)) /
          100
        ).toFixed(2);
      }
      recipient_values.price.unit_price =
        recipient_values?.denomination?.value *
        recipient_values?.product_to_base_conversion;
      recipient_values.price.final_amount =
        parseFloat(recipient_values?.price?.currencyValue) +
        (recipient_values?.price?.surcharged_value !== null
          ? parseFloat(recipient_values?.price?.surcharged_value)
          : 0) -
        (recipient_values?.price?.discounted_value !== null
          ? parseFloat(recipient_values?.price?.discounted_value)
          : 0);
      recipient_values.total =
        recipient_values?.price?.unit_price * recipient_values?.quantity;
    } else {
      recipient_values.price.currencyValue = 0.0;
      recipient_values.price.discounted_value = 0.0;
      recipient_values.price.surcharged_value = 0.0;
      recipient_values.price.unit_price = 0.0;
      recipient_values.price.final_amount = 0.0;
    }
    itemTotalSum += parseFloat(recipient_values.price.final_amount);
    calculatePayable(itemTotalSum, recipient_values.name);
    setRecipient(recipient_values);
  };

  const onChangeCountry = (value) => {
    setSelectedCountry(value);
    setSelectedProduct({});
    setRecipient({
      ...SINGLE_RECIPIENT_PRO,
      name: recipient?.name,
      receiver_name: recipient?.receiver_name,
      email: recipient?.email,
      to_phone_code: recipient?.to_phone_code,
      to_phone_number: recipient?.to_phone_number,
      price: {
        currencyValue: 0,
        discounted_value: 0,
        surcharged_value: 0,
        final_amount: 0,
        unit_price: 0,
      },
    });
    setCodesQuantity(1);
  };

  const handleQuantityChange = (action, value) => {
    let error_quantity = "";
    if (action === "add") {
      if (rewardType === 2 && codeQuantity >= 15 && sendXoxoCodePdf === 1) {
        error_quantity = "Max. quantity is 15.";
      } else if (
        rewardType === 4 &&
        recipient.max_order_quantity == 1 &&
        codeQuantity > recipient.max_order_quantity
      )
        error_quantity = `Max. quantity is ${recipient.max_order_quantity}.`;
      else if (
        rewardType === 4 &&
        ((sendXoxoCodePdf && codeQuantity >= maxPlumProOrderQuantityWithPdf) ||
          codeQuantity >= maxPlumProOrderQuantity)
      ) {
        if (sendXoxoCodePdf && codeQuantity >= maxPlumProOrderQuantityWithPdf) {
          error_quantity = `Max. quantity is ${maxPlumProOrderQuantityWithPdf}.`;
        } else if (codeQuantity >= maxPlumProOrderQuantity) {
          error_quantity = `Max. quantity is ${maxPlumProOrderQuantity}.`;
        }
      } else
        setCodesQuantity((q) => {
          setRecipient({ ...recipient, quantity: parseInt(q) + 1 });
          if (rewardType === 4)
            handleItemTotal({ ...recipient, quantity: parseInt(q) + 1 });
          return parseInt(q) + 1;
        });
    } else if (action === "sub") {
      if (parseInt(codeQuantity) > 1) {
        if (
          rewardType === 4 &&
          codeQuantity == selectedProduct.min_order_quantity
        )
          return;
        setCodesQuantity((q) => {
          setRecipient({ ...recipient, quantity: parseInt(q) - 1 });
          if (rewardType === 4)
            handleItemTotal({ ...recipient, quantity: parseInt(q) - 1 });
          return parseInt(q) - 1;
        });
      }
    } else {
      if (rewardType === 2 && value > 15 && sendXoxoCodePdf === 1) {
        error_quantity = "Max. quantity is 15.";
      } else if (rewardType === 2 && value > 200) {
        error_quantity = "Max. quantity is 200.";
      } else if (
        rewardType === 4 &&
        recipient.max_order_quantity == 1 &&
        value > recipient.max_order_quantity
      ) {
        error_quantity = `Max. quantity is ${recipient.max_order_quantity}.`;
      } else if (
        rewardType === 4 &&
        ((sendXoxoCodePdf && value > maxPlumProOrderQuantityWithPdf) ||
          value > maxPlumProOrderQuantity)
      ) {
        if (sendXoxoCodePdf && value > maxPlumProOrderQuantityWithPdf) {
          error_quantity = `Max. quantity is ${maxPlumProOrderQuantityWithPdf}.`;
        } else if (value > maxPlumProOrderQuantity) {
          error_quantity = `Max. quantity is ${maxPlumProOrderQuantity}.`;
        }
      } else if (value === "")
        setError((error) => {
          return { ...error, quantity: "Min order quantity is 1" };
        });
      setCodesQuantity(value);
      setRecipient((prevRecipient) => {
        return { ...prevRecipient, quantity: value };
      });
      if (rewardType === 4) handleItemTotal({ ...recipient, quantity: value });
    }
    setError({ ...error, quantity: error_quantity });
  };

  useEffect(() => {
    setRecipient((prevState) => {
      return { ...prevState, country: selectedCountry };
    });
  }, [selectedCountry]);

  useEffect(() => {
    setIsdCodes();
    getPhonecodeBasedIP();
  }, []);

  useEffect(() => {
    setError({});
    if (sendMode?.includes("whatsapp")) {
      let recipientState = { ...recipient };
      recipientState["to_phone_code"] = indiaPhoneCode[0];
      setRecipient({ ...recipientState });
    }
  }, [sendMode]);

  useEffect(() => {
    let total_value = recipient.total;

    if (rewardType == 2) {
      total_value = codeQuantity * recipient.codeValue;
    }
    if (rewardType === 4) {
      total_value = recipient.denomination?.value
        ? codeQuantity * recipient.price?.unit_price
        : 0;
    }
    if (recipient.voucherDetail || rewardType === 2) {
      setRecipient({ ...recipient, total: total_value });
    } else {
      setRecipient({
        ...recipient,
        country: selectedCountry,
        price: {
          currencyValue: 0,
          discounted_value: 0,
          surcharged_value: 0,
          final_amount: 0,
          unit_price: 0,
        },
      });
    }
  }, [codeQuantity, recipient.voucherDetail]);

  useEffect(() => {
    if (showSettings) {
      const { current } = settingsRef;
      if (current !== null) {
        current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [showSettings]);

  useEffect(() => {
    if (isEditUserEnabled) {
      const data = recipientData.find((item) => item.id === editUserId);
      const userPrefilledData = {
        ...data,
      };
      setCodesQuantity(data?.quantity);
      setRecipient(userPrefilledData);
      setInitialTotal(data?.total);
      setSelectedDenomination(data?.denomination);
      if (rewardType === 4) {
        setSelectedCountry(data?.country);
        setSelectedProduct(data?.voucherDetail);
      }
      if (
        (data?.date && data.date !== moment().format("DD-MM-YYYY")) ||
        (data?.user_message && data.user_message !== "") ||
        (data?.notes && data.notes !== "") ||
        (data?.po_number && data.po_number !== "")
      ) {
        setShowSettings(true);
      }
    }

    if (!isEditUserEnabled) setShowSettings(false);
  }, [isEditUserEnabled]);

  return (
    <>
      {(isEditUserEnabled || addMore) && (
        <div className="mb-12 mx-24 pt-24">
          {isEditUserEnabled && (
            <Typography.ButtonText>Edit Recipient</Typography.ButtonText>
          )}
          {addMore && (
            <Typography.ButtonText>Add Another Recipient</Typography.ButtonText>
          )}
        </div>
      )}
      {/* User details input */}
      {rewardType === 4 && (
        <Typography.LabelText className="pl-24" color="dark" shade="c200">
          Recipient Details
        </Typography.LabelText>
      )}
      <StyledDivGap className="d-flex mt-12 flex-wrap align-items-center px-24">
        <StyledInput
          id="recepient-name"
          name={rewardType !== 4 ? "name" : "receiver_name"}
          type="text"
          placeholder="Enter Recipient Name"
          className="hide-limit"
          limit={70}
          autoFocus
          value={removeSplCharacters(
            rewardType !== 4 ? recipient.name : recipient.receiver_name
          )}
          onChange={inputChangeHandler}
          error={
            error?.name ||
            error?.receiver_name ||
            recipient?.name?.length >= 70 ||
            recipient?.receiver_name?.length >= 70
          }
          helperText={
            error?.name ||
            error?.receiver_name ||
            (rewardType !== 4
              ? recipient?.name?.length >= 70
              : recipient?.receiver_name?.length >= 70)
              ? "Maximum character limit is 70."
              : ""
          }
        />

        {(sendMode?.includes("email") || rewardType === 4) && (
          <StyledInput
            id="recepient-email"
            name="email"
            type="email"
            limit={150}
            placeholder="Enter Recipient Email Address"
            className="hide-limit"
            onChange={inputChangeHandler}
            error={error.email}
            helperText={error.email}
            value={recipient.email}
          />
        )}
        {(sendMode?.includes("sms") ||
          sendMode?.includes("whatsapp") ||
          rewardType === 4) && (
          <StyledDiv className="d-flex flex-row ">
            <StyledDropDownContainer>
              <Dropdown
                id="phone-code"
                name="to_phone_code"
                placeholder={"ISD"}
                isSearchable={true}
                noOptionsMessage={() => {
                  return "No Phone Code Available";
                }}
                value={recipient.to_phone_code}
                error={error.to_phone_code}
                helperText={error.to_phone_code}
                onChange={inputSelectHandler}
                options={
                  sendMode?.includes("whatsapp")
                    ? indiaPhoneCode
                    : phoneCodeOptions
                }
                className={`${error.to_phone_number ? "mt-20" : ""}`}
              />
            </StyledDropDownContainer>
            <StyledPhoneInput
              id="recepient-phone-number"
              name="to_phone_number"
              type="phone"
              limit={16}
              placeholder="Enter Mobile Number"
              className={`hide-limit ${error.to_phone_number ? "mt-20" : ""}`}
              value={recipient.to_phone_number}
              error={error.to_phone_number}
              helperText={error.to_phone_number}
              onChange={inputChangeHandler}
            />
          </StyledDiv>
        )}
      </StyledDivGap>

      {/* point details input */}
      {rewardType === 1 && (
        <StyledDivGap className="d-flex align-items-baseline w-full px-24 mt-4">
          <StyledInput
            id="value-of-reward"
            type="text"
            name="amount"
            placeholder="Enter Value of Points"
            onChange={inputChangeHandler}
            error={error.amount}
            helperText={
              error.amount
                ? error.amount
                : `1 Reward Points = ${currencyCode} 1.00`
            }
            value={recipient.amount}
          />
          <Typography.ButtonText>
            Total: {currencyCode} {parseFloat(recipient.total).toLocaleString()}
          </Typography.ButtonText>
        </StyledDivGap>
      )}
      {/* code details input */}
      {rewardType === 2 && (
        <StyledDivGap className="d-flex align-items-baseline w-full px-24 mt-4">
          <StyledInput
            id="value-of-reward"
            type="text"
            name="codeValue"
            prefix={currencyCode}
            placeholder="Enter Value of Code"
            onChange={inputChangeHandler}
            error={error.codeValue}
            helperText={error.codeValue}
            value={recipient.codeValue}
          />
          <StyledNoOfCodesInput
            type="number"
            name="quantity"
            id="no-of-codes"
            onChange={(e) => handleQuantityChange("text", e.target.value)}
            prefix={
              <Icon
                iconName="PlumMinus"
                color="dark"
                shade="c200"
                onClick={() => handleQuantityChange("sub")}
              />
            }
            suffix={
              <Icon
                iconName="PlumPlus"
                color="dark"
                shade="c200"
                onClick={() => handleQuantityChange("add")}
              />
            }
            error={error.quantity}
            helperText={error.quantity}
            limit={15}
            className={`hide-limit text-center ${
              error.codeValue ? "codeValue-error" : ""
            }`}
            value={codeQuantity.toLocaleString("en-US", {
              minimumIntegerDigits: 2,
              useGrouping: false,
            })}
          />
          <Typography.LabelText>
            Total: {currencyCode} {parseFloat(recipient.total).toLocaleString()}
          </Typography.LabelText>
        </StyledDivGap>
      )}
      {/* plum pro */}
      {rewardType === 4 && (
        <>
          <Typography.LabelText
            className="pl-24 mt-12"
            color="dark"
            shade="c200"
          >
            Voucher Details
          </Typography.LabelText>
          <StyledDivGap className="d-flex align-items-baseline w-full px-24 flex-wrap mt-12">
            <StyledDropdown
              value={selectedCountry}
              onChange={(e) => {
                onChangeCountry(e);
                setSelectedProduct({});
              }}
              options={countryList}
              name="country"
              isDisabled={false}
              isSearchable={true}
              placeholder={"Select Country"}
              autoFocus
              isLoading={countryListLoading}
              id="plum-pro-select-country"
            />
            <StyledDropdown
              id={"plum-pro-recipent-product"}
              value={recipient.voucherDetail}
              onChange={(value) => {
                onProductChange(value);
                setRecipient({
                  ...recipient,
                  voucherDetail: value,
                  denomination: "",
                });
              }}
              isLoading={productLoader}
              options={voucherList}
              name="voucher"
              isDisabled={productLoader}
              isSearchable={true}
              placeholder={"Select Product"}
              noOptionsMessage={() => {
                return selectedCountry
                  ? "No Product Available"
                  : "Please Select Country First";
              }}
              error={error.voucherDetail}
              helperText={
                <Dropdown.HelperText>{error.voucherDetail}</Dropdown.HelperText>
              }
            />
            {recipient.is_variable ? (
              <StyledInput
                id={"plum-pro-recipent-denomination"}
                placeholder="Enter the value of Product"
                value={recipient.denomination?.label}
                onChange={(e) => {
                  const value = e.target.value?.replace(/[^0-9+]/g, "");
                  onChangeDenomination({
                    label: value,
                    value: parseInt(value),
                  });
                }}
                error={error.denomination}
                helperText={
                  selectedProduct?.min_price
                    ? `Between ${selectedProduct.min_price} and ${selectedProduct.max_price}`
                    : null
                }
                isDisabled={
                  recipient.denominationOption?.length > 0 ? false : true
                }
              />
            ) : (
              <StyledDropdown
                placeholder="Enter the value of Product"
                id={"plum-pro-recipent-denomination"}
                value={recipient.denomination}
                onChange={onChangeDenomination}
                options={recipient.denominationOption}
                name="denomination"
                isDisabled={
                  recipient.denominationOption?.length > 0 ? false : true
                }
                isSearchable={true}
                noOptionsMessage={() => {
                  return "Please Select Brand First";
                }}
                error={error.denomination}
                helperText={
                  <Dropdown.HelperText>
                    {error.denomination}
                  </Dropdown.HelperText>
                }
              />
            )}
            <StyledNoOfCodesInput
              type="number"
              name="quantity"
              id="no-of-codes"
              onChange={(e) =>
                handleQuantityChange("text", parseInt(e.target.value))
              }
              prefix={
                <Icon
                  iconName="PlumMinus"
                  color="dark"
                  shade="c200"
                  onClick={() => {
                    if (!(recipient.max_order_quantity === 1))
                      handleQuantityChange("sub");
                  }}
                />
              }
              suffix={
                <Icon
                  iconName="PlumPlus"
                  color="dark"
                  shade="c200"
                  onClick={() => {
                    if (!(recipient.max_order_quantity === 1))
                      handleQuantityChange("add");
                  }}
                />
              }
              disabled={recipient.max_order_quantity === 1}
              error={error.quantity}
              helperText={error.quantity}
              limit={15}
              className={`hide-limit`}
              value={codeQuantity?.toLocaleString("en-US", {
                minimumIntegerDigits: 2,
                useGrouping: false,
              })}
            />
            <Typography.ButtonText component="div" className="d-flex">
              Total: {currencyCode}{" "}
              {recipient?.price.final_amount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}{" "}
              <div className="ml-2 mt-2" id="UncontrolledTooltipExample">
                <Icon iconName="Info" size="s" />
              </div>
              <UncontrolledTooltip
                placement="bottom"
                target="UncontrolledTooltipExample"
              >
                {priceBreakupMemo}
              </UncontrolledTooltip>
            </Typography.ButtonText>
          </StyledDivGap>
        </>
      )}
      {rewardType === 4 && (
        <div className="px-24">
          <BrandApprovalWarningBanner kybStatus={kybStatus} />
        </div>
      )}
      {/* advance details check box */}
      {!(rewardType === 3 && sendMode === "sms") ? (
        <div className="d-flex w-100 align-items-center px-24 pt-24">
          <StyledCheckbox
            checked={showSettings}
            onChange={() => {
              setShowSettings(!showSettings);
            }}
            value="01"
            label="Show advanced details"
          />
        </div>
      ) : null}

      {/* Advance details */}
      {showSettings && (
        <div className="p-24" ref={settingsRef}>
          {(rewardType === 1 || rewardType === 2) && (
            <div>
              <div style={{ width: "50%" }}>
                <DatePicker
                  components={{
                    OpenPickerIcon: getCustomIcon,
                  }}
                  onChange={handleDateChange}
                  placeholder={""}
                  value={
                    recipient.date ? moment(recipient.date, "DD-MM-YYYY") : ""
                  }
                  label="Scheduled Date"
                  inputFormat="dd-MM-yyyy"
                  disablePast
                  id="xoxo-code-delivery-date"
                  slotProps={{
                    textField: {
                      helperText:
                        "Reward amount will be deducted on scheduled date.",
                    },
                  }}
                />
                <Typography.HelperText color="dark" shade="c200">
                  Reward amount will be deducted on scheduled date.
                </Typography.HelperText>
              </div>
            </div>
          )}
          {rewardType === 4 && (
            <Input
              type="text"
              name="po_number"
              limit={30}
              id={"plum-pro-po-number"}
              placeholder="Enter PO Number(optional)"
              onBlur={(e) => validatePoNumber(e)}
              value={recipient.po_number}
              onChange={inputChangeHandler}
              error={error.po_number}
              helperText={error.po_number}
            />
          )}
          {(sendMode?.includes("email") || rewardType === 4) && (
            <div className="mt-20">
              <Typography.LabelText className="mb-4" color="dark" shade="c200">
                Personal Message to Recipient (Email)
              </Typography.LabelText>
              <ReactQuillEditor
                id="personal-email-message"
                name="user_message"
                label=""
                placeholder="Craft a personal message to your recipient (optional)"
                limit={600}
                prefix={<Icon iconName="PlumNotes" size="s" />}
                value={recipient.user_message}
                onChange={(value) => {
                  setRecipient({ ...recipient, user_message: value });
                }}
                height={100}
              />
            </div>
          )}
          {(rewardType === 1 || rewardType === 2 || rewardType === 3) && (
            <div className="mt-20">
              <Input
                id="personal-email-notes"
                name="notes"
                label="Notes for Internal Reference"
                placeholder="Add a note for internal reference (optional)"
                limit={150}
                prefix={<Icon iconName="PlumNotes" size="s" />}
                value={recipient.notes}
                onChange={inputChangeHandler}
              />
            </div>
          )}
        </div>
      )}
      <hr className="mx-24"></hr>
      {/* Buttons */}

      <StyledDivGap className="d-flex justify-content-end p-24">
        {(addMore || isEditUserEnabled) && (
          <Button color="primary" onClick={handleCancel} variant="plain">
            Cancel
          </Button>
        )}

        {isEditUserEnabled ? (
          <Button
            color="primary"
            onClick={validateData}
            variant="outlined"
            disabled={isValidating}
            loading={isValidating}
          >
            Save Changes
          </Button>
        ) : (
          <Button
            color="primary"
            onClick={validateData}
            variant="outlined"
            disabled={isValidating}
            loading={isValidating}
          >
            Add Recipient
          </Button>
        )}
      </StyledDivGap>
    </>
  );
};

export default RecepientForm;
